import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { Card, Table, Pagination, Btn, Modal, Input, InfoAlert, PageHeader, Badge, StatusBadge } from '../components/UI';
import { IconPlus, IconBan, IconDevice, IconRefresh } from '../components/Icons';

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(null);
  const [mac, setMac]         = useState('');
  const [duration, setDuration] = useState('60');
  const [busy, setBusy]       = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.getDevices({page,limit:20}); setDevices(r.devices||[]); setTotal(r.total||0); }
    catch(e) { toast(e.message,'error'); }
    finally { setLoading(false); }
  },[page]);

  useEffect(()=>{ load(); },[load]);

  const revoke = async (mac) => {
    // Revoking also detaches the device from the plan, so say so before doing it.
    if (!window.confirm(`Revoke ${mac}?\n\nThis cuts its access AND removes it from the user's plan. They would need to add it again.`)) return;
    try { const r = await api.revokeDevice(mac); toast(r.message || `Device ${mac} revoked`,'success'); load(); }
    catch(e) { toast(e.message,'error'); }
  };

  const authorize = async () => {
    setBusy(true);
    try { await api.authorizeDevice(mac, parseInt(duration)); toast(`Authorized for ${duration} mins`,'success'); setModal(null); setMac(''); }
    catch(e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  const columns = [
    { key:'mac',  label:'MAC Address', render: d=><code style={{ fontFamily:'Geist Mono,monospace', fontSize:12, color:'var(--accent)' }}>{d.mac_address}</code> },
    { key:'nick', label:'Nickname',    render: d=><span style={{ fontSize:13 }}>{d.nickname||'—'}</span> },
    { key:'user', label:'Owner', render: d=> d.user
      ? <div><div style={{ fontSize:13, fontWeight:500 }}>{d.user.name}</div><div style={{ fontSize:11, color:'var(--text3)' }}>{d.user.email}</div></div>
      : <span style={{ color:'var(--text3)' }}>—</span>
    },
    { key:'plan',   label:'Plan',       render: d=> d.subscription?.plan ? <code style={{ fontSize:11, color:'var(--text2)', fontFamily:'Geist Mono,monospace' }}>{d.subscription.plan}</code> : '—' },
    { key:'status', label:'Sub Status', render: d=> d.subscription?.status ? <StatusBadge status={d.subscription.status} /> : '—' },
    { key:'type',   label:'Type',       render: d=> d.is_primary ? <Badge color="blue">Primary</Badge> : <Badge color="gray">Add-on</Badge> },
    { key:'actions',label:'Actions',    render: d=><Btn size="xs" variant="ghost" icon={IconBan} style={{ color:'var(--red)' }} onClick={()=>revoke(d.mac_address)}>Revoke</Btn> },
  ];

  const mobileCard = (d) => (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div>
          <code style={{ fontSize:12, color:'var(--accent)', fontFamily:'Geist Mono,monospace' }}>{d.mac_address}</code>
          {d.nickname && <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{d.nickname}</div>}
        </div>
        {d.is_primary ? <Badge color="blue">Primary</Badge> : <Badge color="gray">Add-on</Badge>}
      </div>
      {d.User && <div style={{ fontSize:12, marginBottom:6 }}>{d.User.name} <span style={{ color:'var(--text3)' }}>({d.User.email})</span></div>}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        {d.subscription?.plan && <code style={{ fontSize:11, color:'var(--text2)' }}>{d.subscription.plan}</code>}
        <Btn size="xs" variant="ghost" icon={IconBan} style={{ color:'var(--red)' }} onClick={()=>revoke(d.mac_address)}>Revoke</Btn>
      </div>
    </>
  );

  return (
    <div className="page-enter">
      <PageHeader title="Devices" subtitle={`${total.toLocaleString()} registered devices`}
        action={
          <div style={{ display:'flex', gap:8 }}>
            <Btn size="sm" variant="ghost" icon={IconRefresh} onClick={load}>Refresh</Btn>
            <Btn size="sm" icon={IconPlus} onClick={()=>setModal('authorize')}>Authorize</Btn>
          </div>
        }
      />

      <Card style={{ padding:0, overflow:'hidden' }}>
        <Table columns={columns} rows={devices} loading={loading} emptyText="No devices found." mobileCard={mobileCard} />
        <div style={{ borderTop:'1px solid var(--border)', padding:'0 14px' }}>
          <Pagination page={page} total={total} limit={20} onChange={setPage} />
        </div>
      </Card>

      {modal==='authorize' && (
        <Modal title="Manually Authorize Device" onClose={()=>setModal(null)}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <Input label="MAC Address" value={mac} onChange={setMac} placeholder="xx:xx:xx:xx:xx:xx" hint="Device to authorize on Omada." required />
            <Input label="Duration (minutes)" type="number" value={duration} onChange={setDuration} />
            <InfoAlert type="warning">⚠ This authorizes directly on Omada — no subscription or charge is created. Use for testing or emergency access.</InfoAlert>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={()=>setModal(null)}>Cancel</Btn>
              <Btn icon={IconDevice} onClick={authorize} loading={busy} disabled={!mac}>Authorize</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}